// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { ChargeItem } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getPatientPaymentMethod } from '@polarissleep/shared';
import { IconCheck, IconCreditCard, IconPlus, IconShield } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import {
  type InsuredBalanceStatus,
  PAYMENT_ARCHIVED_EXTENSION_URL,
  PAYMENT_CLAIM_STATUS_EXTENSION_URL,
  PAYMENT_HISTORY_EXTENSION_URL,
  PAYMENT_MANUAL_PAID_EXTENSION_URL,
  PAYMENT_OUTREACH_ATTEMPTS_EXTENSION_URL,
  type PaymentChargeEntry,
  type PaymentChargesFilters,
  type PaymentSource,
  type PaymentsTimeframe,
  usePaymentCharges,
} from '../../hooks/usePaymentCharges';
import { botIdentifier } from '../../lib/bot-identifier';
import { formatMoney } from '../../utils/chargeitems';
import styles from './PatientPaymentsTab.module.css';
import { formatStatus, type PaymentRowAction, PaymentsSection } from './sections/PaymentsSection';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All charges' },
  {
    value: 'self-pay',
    label: (
      <>
        <IconCreditCard size={14} />
        Self-pay
      </>
    ),
  },
  {
    value: 'insured',
    label: (
      <>
        <IconShield size={14} />
        Insured
      </>
    ),
  },
];

const STATUS_FILTER_ORDER = [
  'Awaiting Claim Submission',
  'Awaiting Claim Return',
  'Record Balance',
  'Awaiting Payment',
  'Paid',
  'Refunded',
  'Archived with Balance',
];

function addDays(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatBillingDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value.slice(0, 10)
    : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

function editableStatus(entry: PaymentChargeEntry): string {
  if (entry.status === 'Planned') {
    return 'planned';
  }
  if (entry.status === 'Paid') {
    return 'paid';
  }
  if (entry.status === 'Refunded') {
    return 'refunded';
  }
  if (entry.status === 'Archived') {
    return 'archived';
  }
  return 'billed';
}

function reviewStatus(entry: PaymentChargeEntry): string {
  if (entry.source !== 'insured') {
    return editableStatus(entry);
  }
  if (entry.claimStatus) {
    return entry.claimStatus;
  }
  if (entry.history.some((item) => item.event.toLowerCase().includes('claim returned'))) {
    return 'record';
  }
  if (entry.history.some((item) => item.event.toLowerCase().includes('claim submitted'))) {
    return 'sent';
  }
  if (entry.balanceStatus === 'Awaiting balance') {
    return 'toSubmit';
  }
  return editableStatus(entry);
}

function BalanceDrawer({
  entry,
  opened,
  onClose,
  onSaved,
}: {
  entry: PaymentChargeEntry | undefined;
  opened: boolean;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const medplum = useMedplum();
  const [amount, setAmount] = useState('');
  const [insurance, setInsurance] = useState('');
  const [dueDate, setDueDate] = useState(addDays(30));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const open = opened && !!entry;

  useEffect(() => {
    if (!open || !entry) {
      return;
    }
    setAmount(entry.balanceCents ? (entry.balanceCents / 100).toFixed(2) : '');
    setInsurance(
      entry.insuranceContributionCents === undefined ? '' : (entry.insuranceContributionCents / 100).toFixed(2)
    );
    setDueDate(entry.dueDate?.slice(0, 10) ?? addDays(30));
    setNote('');
  }, [open, entry]);

  const save = async (): Promise<void> => {
    if (!entry?.encounterId) {
      return;
    }
    const patientCents = Math.round(Number(amount) * 100);
    const insuranceCents = insurance.trim() ? Math.round(Number(insurance) * 100) : undefined;
    if (!Number.isInteger(patientCents) || patientCents <= 0) {
      showNotification({ color: 'red', message: 'Enter an amount greater than $0.' });
      return;
    }
    if (insuranceCents !== undefined && (!Number.isInteger(insuranceCents) || insuranceCents < 0)) {
      showNotification({ color: 'red', message: 'Enter a valid insurance contribution.' });
      return;
    }
    setSaving(true);
    try {
      await medplum.executeBot(botIdentifier('create-patient-bill'), {
        encounterId: entry.encounterId,
        patientResponsibilityCents: patientCents,
        insuranceContributionCents: insuranceCents,
        dueDate,
        note: note.trim() || undefined,
      });
      showNotification({ color: 'green', message: 'Patient balance recorded and notification queued.' });
      onSaved();
      onClose();
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const paid = entry?.balanceStatus === ('Paid' satisfies InsuredBalanceStatus);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <div>
          <Text fw={700} size="xl" lh={1.2}>
            Record patient balance
          </Text>
          {entry && (
            <Text size="sm" c="dimmed" mt={4}>
              {entry.patientName} · {entry.patientIdentifier} · {entry.payer ?? 'Insurance'} · charge {entry.chargeId}
            </Text>
          )}
        </div>
      }
      centered
      size={560}
      classNames={{
        content: styles.modalContent,
        header: styles.modalHeader,
        title: styles.modalTitle,
        body: styles.modalBody,
      }}
    >
      {entry && (
        <Stack>
          {!paid && (
            <Stack gap={0} className={styles.detailRows}>
              <div className={styles.detailRow}>
                <Text c="dimmed">Charge for</Text>
                <Text fw={600}>{entry.encounterType ?? '—'}</Text>
              </div>
              <div className={styles.detailRow}>
                <Text c="dimmed">Date of service</Text>
                <Text fw={600}>{formatBillingDate(entry.dateOfService)}</Text>
              </div>
              <div className={styles.detailRow}>
                <Text c="dimmed">Claim amount</Text>
                <Text fw={600}>{formatMoney(entry.billedCents / 100)}</Text>
              </div>
              {entry.history.some((item) => item.event.toLowerCase().includes('claim submitted')) && (
                <div className={styles.detailRow}>
                  <Text c="dimmed">Submitted / returned</Text>
                  <Text fw={600}>
                    {formatBillingDate(
                      entry.history.find((item) => item.event.toLowerCase().includes('claim submitted'))?.date
                    )}{' '}
                    ·{' '}
                    {formatBillingDate(
                      entry.history.find((item) => item.event.toLowerCase().includes('claim returned'))?.date
                    )}
                  </Text>
                </div>
              )}
            </Stack>
          )}
          {paid ? (
            <Paper withBorder p="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600}>Payment receipt</Text>
                  <Badge color="green">Paid</Badge>
                </Group>
                <Text>Patient responsibility: {formatMoney(entry.billedCents / 100)}</Text>
                <Text>
                  Insurance contribution:{' '}
                  {entry.insuranceContributionCents === undefined
                    ? '—'
                    : formatMoney(entry.insuranceContributionCents / 100)}
                </Text>
              </Stack>
            </Paper>
          ) : (
            <>
              <TextInput
                label="Patient responsibility"
                leftSection="$"
                type="number"
                min={0}
                step={0.01}
                value={amount}
                onChange={(event) => setAmount(event.currentTarget.value)}
                required
              />
              <Text size="xs" c="dimmed" mt={-8}>
                Copay, coinsurance, and/or deductible after adjudication.
              </Text>
              <TextInput
                label="Amount insurance paid"
                leftSection="$"
                type="number"
                min={0}
                step={0.01}
                value={insurance}
                onChange={(event) => setInsurance(event.currentTarget.value)}
              />
              <TextInput
                label="Due date"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.currentTarget.value)}
                required
              />
              <Textarea
                label="Note"
                placeholder="Optional — internal note or context for the patient's receipt."
                value={note}
                onChange={(event) => setNote(event.currentTarget.value)}
                minRows={3}
              />
              <Paper withBorder p="md" className={styles.balancePreview}>
                <Group justify="space-between" align="center">
                  <Text size="sm" fw={600}>
                    Patient will be billed
                  </Text>
                  <Text fw={800} size="xl">
                    {formatMoney(Number(amount) || 0)}
                  </Text>
                </Group>
              </Paper>
              <Group justify="space-between" align="flex-end" className={styles.modalFooter}>
                <Text size="xs" c="dimmed" maw={230}>
                  A balance record is filed and the patient is emailed to pay.
                </Text>
                <Button loading={saving} leftSection={<IconPlus size={16} />} onClick={save}>
                  Record & notify patient
                </Button>
              </Group>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
}

function appendExtension(
  charge: ChargeItem,
  url: string,
  value: { valueBoolean: boolean } | { valueCode: string } | { valueInteger: number }
): ChargeItem {
  return {
    ...charge,
    extension: [...(charge.extension ?? []).filter((extension) => extension.url !== url), { url, ...value }],
  };
}

function appendHistory(charge: ChargeItem, event: string): ChargeItem {
  return {
    ...charge,
    extension: [
      ...(charge.extension ?? []),
      {
        url: PAYMENT_HISTORY_EXTENSION_URL,
        extension: [
          { url: 'date', valueDateTime: new Date().toISOString() },
          { url: 'event', valueString: event },
        ],
      },
    ],
  };
}

function PaymentActionModal({
  entry,
  action,
  opened,
  onClose,
  onChanged,
  onRefundRequested,
  onClaimReturned,
}: {
  entry: PaymentChargeEntry | undefined;
  action: PaymentRowAction | undefined;
  opened: boolean;
  onClose: () => void;
  onChanged: () => void;
  onRefundRequested: () => void;
  onClaimReturned: () => void;
}): JSX.Element {
  const medplum = useMedplum();
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('billed');
  const [attempts, setAttempts] = useState<number | string>(0);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState<number | string>(0);
  const [refundAmount, setRefundAmount] = useState<number | string>(0);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<ReturnType<typeof getPatientPaymentMethod>>();
  const open = opened && !!entry && !!action;

  useEffect(() => {
    if (!entry) {
      return;
    }
    setAttempts(entry.outreachAttempts);
    setAmount(entry.balanceCents / 100);
    setRefundAmount((entry.receivedCents - entry.refundedCents) / 100);
    setStatus(reviewStatus(entry));
    setReason('');
    setAdjustmentOpen(action === 'adjust' || action === 'review');
  }, [entry, action]);

  useEffect(() => {
    let cancelled = false;
    setPaymentMethod(undefined);
    if (!entry?.patientId || !open || entry.source !== 'self-pay') {
      return;
    }
    medplum
      .readResource('Patient', entry.patientId)
      .then((patient) => {
        if (!cancelled) {
          setPaymentMethod(getPatientPaymentMethod(patient));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [entry, medplum, open]);

  const close = (): void => {
    if (!saving) {
      onClose();
    }
  };

  const updateCharge = async (
    mutate: (charge: ChargeItem) => ChargeItem,
    message: string,
    after?: () => void
  ): Promise<void> => {
    if (!entry?.chargeItemId || entry.chargeItemId.startsWith('encounter:')) {
      showNotification({
        color: 'red',
        message: 'This row is a synthetic insurance work-queue entry. Record its balance first.',
      });
      return;
    }
    setSaving(true);
    try {
      const charge = await medplum.readResource('ChargeItem', entry.chargeItemId);
      await medplum.updateResource(mutate(charge));
      showNotification({ color: 'green', message });
      onChanged();
      after?.();
      onClose();
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const markPaid = (): Promise<void> =>
    updateCharge(
      (charge) =>
        appendHistory(
          appendExtension(charge, PAYMENT_MANUAL_PAID_EXTENSION_URL, { valueBoolean: true }),
          'Payment marked paid manually'
        ),
      'Payment marked as paid.'
    );

  const archive = (): Promise<void> =>
    updateCharge(
      (charge) =>
        appendExtension(
          appendHistory(
            { ...charge, status: 'aborted' },
            `Archived with ${entry?.outreachAttempts ?? 0} outreach attempts${reason.trim() ? ` — ${reason.trim()}` : ''}`
          ),
          PAYMENT_ARCHIVED_EXTENSION_URL,
          { valueBoolean: true }
        ),
      'Payment archived.'
    );

  const saveAdjustment = (): Promise<void> =>
    updateCharge((charge) => {
      const claimStatus = ['toSubmit', 'sent', 'record'].includes(status) ? status : undefined;
      let chargeStatus: ChargeItem['status'] = 'billed';
      if (status === 'planned') {
        chargeStatus = 'planned';
      } else if (status === 'archived') {
        chargeStatus = 'aborted';
      }
      let updated: ChargeItem = {
        ...charge,
        status: chargeStatus,
        extension: (charge.extension ?? []).filter((extension) => extension.url !== PAYMENT_CLAIM_STATUS_EXTENSION_URL),
      };
      if (claimStatus) {
        updated = appendExtension(updated, PAYMENT_CLAIM_STATUS_EXTENSION_URL, { valueCode: claimStatus });
      }
      updated = appendExtension(updated, PAYMENT_OUTREACH_ATTEMPTS_EXTENSION_URL, {
        valueInteger: Number(attempts) || 0,
      });
      if (status === 'paid') {
        updated = appendExtension(updated, PAYMENT_MANUAL_PAID_EXTENSION_URL, { valueBoolean: true });
      }
      if (status === 'archived') {
        updated = appendExtension(updated, PAYMENT_ARCHIVED_EXTENSION_URL, { valueBoolean: true });
      }
      if (reason.trim()) {
        updated.note = [...(updated.note ?? []), { text: `Manual adjustment: ${reason.trim()}` }];
      }
      return appendHistory(updated, `Manual adjustment${reason.trim() ? `: ${reason.trim()}` : ''}`);
    }, 'Payment adjustment applied.');

  const resend = async (): Promise<void> => {
    if (!entry?.chargeItemId) {
      return;
    }
    setSaving(true);
    try {
      const result = await medplum.executeBot(botIdentifier('resend-patient-payment-notification'), {
        chargeItemId: entry.chargeItemId,
      });
      if (!(result as { sent?: boolean }).sent) {
        throw new Error('The charge is not payable or notification delivery failed.');
      }
      const charge = await medplum.readResource('ChargeItem', entry.chargeItemId);
      await medplum.updateResource(appendHistory(charge, 'Balance bill resent to patient'));
      showNotification({ color: 'green', message: 'Balance bill re-sent and notification queued.' });
      onChanged();
      onClose();
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const logDeclinedAttempt = (): Promise<void> =>
    updateCharge(
      (charge) => appendHistory(charge, 'Card declined — automatic retry scheduled in 3 days'),
      'Declined attempt logged; automatic retry scheduled.'
    );

  const submitClaim = (): Promise<void> =>
    updateCharge(
      (charge) => appendHistory(charge, `Claim submitted to ${entry?.payer ?? 'payer'}`),
      'Claim marked submitted.'
    );

  const claimReturned = (): Promise<void> =>
    updateCharge(
      (charge) => appendHistory(charge, `Claim returned by ${entry?.payer ?? 'payer'}`),
      'Claim marked returned. Record patient balance next.',
      onClaimReturned
    );

  const chargeNow = async (): Promise<void> => {
    if (!entry?.appointmentId) {
      showNotification({ color: 'red', message: 'Charge now requires an appointment-backed payment.' });
      return;
    }
    const requestedAmountCents = Math.round(Number(amount) * 100);
    if (
      !Number.isInteger(requestedAmountCents) ||
      requestedAmountCents <= 0 ||
      requestedAmountCents > entry.balanceCents
    ) {
      showNotification({ color: 'red', message: 'Enter an amount between $0.01 and the balance due.' });
      return;
    }
    setSaving(true);
    try {
      await medplum.executeBot(botIdentifier('confirm-appointment-payment'), {
        appointmentId: entry.appointmentId,
        amountCents: requestedAmountCents,
      });
      showNotification({ color: 'green', message: 'Payment charged successfully.' });
      onChanged();
      onClose();
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const refund = async (): Promise<void> => {
    if (!entry?.hasCapturedPayment) {
      return;
    }
    setSaving(true);
    try {
      const requestedAmountCents = Math.round(Number(refundAmount) * 100);
      await medplum.executeBot(botIdentifier('refund-charge'), {
        chargeItemId: entry.chargeItemId,
        ...(requestedAmountCents === entry.receivedCents - entry.refundedCents
          ? {}
          : { amountCents: requestedAmountCents }),
      });
      if (reason.trim()) {
        const refundedCharge = await medplum.readResource('ChargeItem', entry.chargeItemId);
        await medplum.updateResource(appendHistory(refundedCharge, `Refund reason: ${reason.trim()}`));
      }
      showNotification({ color: 'green', message: 'Refund issued and charge marked refunded.' });
      onChanged();
      onClose();
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const refundMaximum = entry ? Math.max(0, entry.receivedCents - entry.refundedCents) / 100 : 0;

  let title = 'Archive this charge?';
  if (action === 'review' || action === 'adjust') {
    title = 'Review payment';
  } else if (action === 'collect') {
    title = 'Collect self-pay payment';
  } else if (action === 'charge') {
    title = 'Charge card on file';
  } else if (action === 'resend') {
    title = 'Resend balance bill';
  } else if (action === 'paid') {
    title = 'Mark payment paid';
  } else if (action === 'refund') {
    title = 'Refund payment';
  }
  const review = action === 'review' || action === 'adjust';
  let modalSize: number | string = 'md';
  if (review) {
    modalSize = 560;
  } else if (action === 'archive') {
    modalSize = 470;
  }
  let destination = 'outstanding';
  if (status === 'archived') {
    destination = 'archived';
  } else if (status === 'planned' || ['toSubmit', 'sent', 'record'].includes(status)) {
    destination = 'upcoming';
  }

  return (
    <Modal
      opened={open}
      onClose={close}
      title={
        <div>
          <Text fw={700} size="xl" lh={1.2}>
            {review && entry ? `Charge ${entry.chargeId}` : title}
          </Text>
          {review && entry && (
            <Text size="sm" c="dimmed" mt={4}>
              {entry.patientName} · {entry.patientIdentifier} · {entry.source === 'self-pay' ? 'Self-pay' : entry.payer}
            </Text>
          )}
          {!review && entry && (
            <Text size="sm" c="dimmed" mt={4}>
              {entry.patientName} · {entry.patientIdentifier} · charge {entry.chargeId}
            </Text>
          )}
        </div>
      }
      centered
      size={modalSize}
      classNames={{
        content: styles.modalContent,
        header: styles.modalHeader,
        title: styles.modalTitle,
        body: styles.modalBody,
      }}
    >
      {entry && action && (
        <Stack>
          {!review && (
            <Text size="sm" c="dimmed">
              <b>{entry.patientName}</b> · {entry.encounterType} · {formatBillingDate(entry.dateOfService)}
            </Text>
          )}
          {!review && (
            <Group grow>
              <Paper withBorder p="sm">
                <Text size="xs" c="dimmed">
                  Status
                </Text>
                <Text fw={600}>{entry.status}</Text>
              </Paper>
              <Paper withBorder p="sm">
                <Text size="xs" c="dimmed">
                  Balance
                </Text>
                <Text fw={600}>{formatMoney(entry.balanceCents / 100)}</Text>
              </Paper>
              <Paper withBorder p="sm">
                <Text size="xs" c="dimmed">
                  Outreach
                </Text>
                <Text fw={600}>{entry.outreachAttempts}</Text>
              </Paper>
            </Group>
          )}
          {review && (
            <Stack gap={0} className={styles.detailRows}>
              <div className={styles.detailRow}>
                <Text c="dimmed">Status</Text>
                <Stack gap={2} align="flex-end">
                  <Select
                    aria-label="Charge status"
                    data={[
                      ...(entry.source === 'self-pay'
                        ? [{ value: 'planned', label: 'Scheduled' }]
                        : [
                            { value: 'toSubmit', label: 'Awaiting Claim Submission' },
                            { value: 'sent', label: 'Awaiting Claim Return' },
                            { value: 'record', label: 'Record Balance' },
                          ]),
                      { value: 'billed', label: 'Awaiting Payment' },
                      { value: 'paid', label: 'Paid' },
                      { value: 'refunded', label: 'Refunded' },
                      { value: 'archived', label: 'Archived with Balance' },
                    ]}
                    value={status}
                    onChange={(value) => setStatus(value ?? 'billed')}
                    comboboxProps={{ withinPortal: false, position: 'bottom-start', shadow: 'sm' }}
                    w={165}
                    size="xs"
                  />
                  <Text size="xs" c="dimmed">
                    Moves the charge to <b>{destination}.</b>
                  </Text>
                </Stack>
              </div>
              <div className={styles.detailRow}>
                <Text c="dimmed">Encounter type</Text>
                <Text fw={600}>
                  {entry.encounterType ?? '—'}
                  {entry.periodStart && entry.periodEnd ? (
                    <Text size="xs" c="dimmed">
                      {formatBillingDate(entry.periodStart)} – {formatBillingDate(entry.periodEnd)}
                    </Text>
                  ) : null}
                </Text>
              </div>
              <div className={styles.detailRow}>
                <Text c="dimmed">Date of service</Text>
                <Text fw={600}>{formatBillingDate(entry.dateOfService)}</Text>
              </div>
              <div className={styles.detailRow}>
                <Text c="dimmed">Billed</Text>
                <Text fw={600}>{formatMoney(entry.billedCents / 100)}</Text>
              </div>
              {entry.source !== 'self-pay' && (
                <div className={styles.detailRow}>
                  <Text c="dimmed">Insurance paid</Text>
                  <Text fw={600}>
                    {entry.insuranceContributionCents === undefined
                      ? '—'
                      : formatMoney(entry.insuranceContributionCents / 100)}
                  </Text>
                </div>
              )}
              <div className={styles.detailRow}>
                <Text c="dimmed">Received from patient</Text>
                <Text fw={600}>{entry.receivedCents ? formatMoney(entry.receivedCents / 100) : '—'}</Text>
              </div>
              {entry.refundedCents > 0 && (
                <div className={styles.detailRow}>
                  <Text c="dimmed">Refunded</Text>
                  <Text fw={600}>−{formatMoney(entry.refundedCents / 100)}</Text>
                </div>
              )}
              <div className={styles.detailRow}>
                <Text c="dimmed">Balance</Text>
                <Text fw={600}>{formatMoney(entry.balanceCents / 100)}</Text>
              </div>
              {entry.dueDate && (
                <div className={styles.detailRow}>
                  <Text c="dimmed">Due date</Text>
                  <Text fw={600}>{formatBillingDate(entry.dueDate)}</Text>
                </div>
              )}
              <div className={styles.detailRow}>
                <Text c="dimmed">Outreach attempts</Text>
                <Stack gap={2} align="flex-end">
                  <Group gap={0} className={styles.stepper}>
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      disabled={Number(attempts) <= 0}
                      onClick={() => setAttempts(Math.max(0, Number(attempts) - 1))}
                    >
                      −
                    </Button>
                    <NumberInput
                      aria-label="Outreach attempts"
                      value={attempts}
                      onChange={setAttempts}
                      min={0}
                      max={20}
                      hideControls
                      w={58}
                      size="xs"
                    />
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      disabled={Number(attempts) >= 20}
                      onClick={() => setAttempts(Math.min(20, Number(attempts) + 1))}
                    >
                      +
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed" ta="right">
                    Times the care manager has reached out — informational only.
                  </Text>
                </Stack>
              </div>
              {entry.source === 'self-pay' && (
                <div className={styles.detailRow}>
                  <Text c="dimmed">Card on file</Text>
                  <Text fw={600}>
                    {paymentMethod ? `${paymentMethod.brand} ···· ${paymentMethod.last4}` : 'No card on file'}
                  </Text>
                </div>
              )}
              {entry.history.length > 0 && (
                <div className={styles.activity}>
                  <Text fw={700}>Activity</Text>
                  {entry.history.map((item, index) => (
                    <Group key={`${item.date}-${index}`} gap="sm" wrap="nowrap">
                      <Text fw={600} size="xs">
                        {formatBillingDate(item.date)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {item.event}
                      </Text>
                    </Group>
                  ))}
                </div>
              )}
            </Stack>
          )}
          {(action === 'charge' || action === 'collect' || action === 'paid') && (
            <Text>
              Record the full outstanding balance of {formatMoney(entry.balanceCents / 100)} for this payment.
            </Text>
          )}
          {(action === 'charge' || action === 'collect') && (
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Text c="dimmed">Card on file</Text>
                <Text fw={600}>
                  {paymentMethod ? `${paymentMethod.brand} ···· ${paymentMethod.last4}` : 'No card on file'}
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                Single-charge authorization. This does not create recurring consent.
              </Text>
            </Paper>
          )}
          {action === 'resend' && <Text>The patient will receive a fresh balance notification for this charge.</Text>}
          {action === 'archive' && (
            <Stack gap="sm">
              <Paper withBorder p="md" className={styles.warningPanel}>
                <Text fw={600}>Archiving closes this charge with its balance still owed.</Text>
                <Text size="sm">
                  It stops appearing in Outstanding and no further billing or outreach is prompted. You can reopen it
                  later from Manual adjustment.
                </Text>
                <Text size="sm" mt="xs">
                  Outreach attempts logged: <b>{entry.outreachAttempts}</b>
                </Text>
              </Paper>
              <TextInput
                label="Reason"
                placeholder="e.g. referred to collections, patient deceased"
                value={reason}
                onChange={(event) => setReason(event.currentTarget.value)}
                required
              />
            </Stack>
          )}
          {review && adjustmentOpen && (
            <Stack className={styles.adjustmentPanel} gap="sm">
              <Text size="xs" c="dimmed">
                Amounts are fixed once the charge posts — <b>Paid</b> and <b>Refunded</b> are set by the platform when a
                payment captures, an ERA posts, or a refund is issued. What you can correct here is where the charge
                sits and outreach logged outside the app. Every change is written to the activity log.
              </Text>
              <TextInput
                label="Reason for change"
                placeholder="e.g. paid by check at front desk"
                value={reason}
                onChange={(event) => setReason(event.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button
                  variant="default"
                  disabled={
                    status === reviewStatus(entry) && Number(attempts) === entry.outreachAttempts && !reason.trim()
                  }
                  onClick={() => {
                    setStatus(reviewStatus(entry));
                    setAttempts(entry.outreachAttempts);
                    setReason('');
                  }}
                >
                  Reset
                </Button>
                <Button
                  loading={saving}
                  disabled={status === reviewStatus(entry) && Number(attempts) === entry.outreachAttempts}
                  onClick={saveAdjustment}
                >
                  Save changes
                </Button>
              </Group>
            </Stack>
          )}
          {review && entry.estimatedBilledCents !== undefined && entry.estimatedBilledCents !== entry.billedCents && (
            <Paper withBorder p="md">
              <Stack>
                <Text size="sm">
                  Estimated: {formatMoney(entry.estimatedBilledCents / 100)} · Actual:{' '}
                  {formatMoney(entry.billedCents / 100)}
                </Text>
              </Stack>
            </Paper>
          )}
          {review && entry.source === 'insured' && entry.rpmDetails && (
            <Paper withBorder p="md">
              <Text fw={600} mb="xs">
                RPM details
              </Text>
              <Text size="sm">
                Transmission days: {entry.rpmDetails.transmissionDays}/16 ·{' '}
                {entry.rpmDetails.transmissionDays >= 16 ? '99454 met' : '99454 not met'}
              </Text>
              <Text size="sm">
                Care-management minutes: {entry.rpmDetails.careManagementMinutes}/20 ·{' '}
                {entry.rpmDetails.careManagementMinutes >= 20 ? '99457 met' : '99457 not met'}
              </Text>
              {entry.rpmDetails.chargeLines.map((line) => (
                <Text size="sm" key={line.code}>
                  {line.code}: {line.amountCents === undefined ? '—' : formatMoney(line.amountCents / 100)} ·{' '}
                  {line.met ? 'met' : 'not met'}
                </Text>
              ))}
            </Paper>
          )}
          {(action === 'charge' || action === 'collect') && (
            <NumberInput label="Amount to charge" prefix="$" min={0} step={0.01} value={amount} onChange={setAmount} />
          )}
          {action === 'refund' && (
            <Stack>
              <Text>Collected: {formatMoney(entry.receivedCents / 100)}</Text>
              <Text>Already refunded: {formatMoney(entry.refundedCents / 100)}</Text>
              <Text>
                Refund to: {paymentMethod ? `${paymentMethod.brand} ···· ${paymentMethod.last4}` : 'Original card'}
              </Text>
              <Text fw={600}>Available to refund: {formatMoney(refundMaximum)}</Text>
              <NumberInput
                label="Refund amount"
                prefix="$"
                min={0.01}
                max={refundMaximum}
                step={0.01}
                value={refundAmount}
                onChange={setRefundAmount}
                disabled={refundMaximum <= 0}
              />
              <Button variant="default" disabled={refundMaximum <= 0} onClick={() => setRefundAmount(refundMaximum)}>
                Refund the full {formatMoney(refundMaximum)}
              </Button>
              <TextInput
                label="Reason"
                placeholder="Optional"
                value={reason}
                onChange={(event) => setReason(event.currentTarget.value)}
              />
            </Stack>
          )}
          <Group justify="flex-end" className={styles.modalFooter}>
            {review && (
              <>
                <Button
                  variant="default"
                  disabled={!entry.hasCapturedPayment || entry.receivedCents <= entry.refundedCents}
                  onClick={onRefundRequested}
                >
                  Refund{entry.receivedCents > entry.refundedCents ? ` up to ${formatMoney(refundMaximum)}` : ''}
                </Button>
                {(!entry.hasCapturedPayment || entry.receivedCents <= entry.refundedCents) && (
                  <Text size="xs" c="dimmed" maw={210}>
                    No payment has been captured on this charge.
                  </Text>
                )}
              </>
            )}
            <Button variant="default" onClick={close}>
              {review ? 'Done' : 'Cancel'}
            </Button>
            {(action === 'charge' || action === 'collect') && (
              <>
                <Button variant="subtle" loading={saving} onClick={logDeclinedAttempt}>
                  Log declined attempt
                </Button>
                <Button variant="default" loading={saving} onClick={resend}>
                  Send balance bill
                </Button>
                <Button loading={saving} leftSection={<IconCheck size={16} />} onClick={chargeNow}>
                  {action === 'collect' ? 'Collect' : 'Charge'} {formatMoney(Number(amount) || 0)}
                </Button>
              </>
            )}
            {action === 'submit' && (
              <Button loading={saving} onClick={submitClaim}>
                Mark submitted
              </Button>
            )}
            {action === 'returned' && (
              <Button loading={saving} onClick={claimReturned}>
                Claim returned
              </Button>
            )}
            {action === 'resend' && (
              <Button loading={saving} onClick={resend}>
                Resend bill
              </Button>
            )}
            {action === 'paid' && (
              <Button loading={saving} onClick={markPaid}>
                Mark paid
              </Button>
            )}
            {action === 'archive' && (
              <Button loading={saving} color="red" onClick={archive}>
                Archive with {formatMoney(entry.balanceCents / 100)} balance
              </Button>
            )}
            {action === 'refund' && (
              <Button
                loading={saving}
                color="red"
                disabled={refundMaximum <= 0 || Number(refundAmount) <= 0 || Number(refundAmount) > refundMaximum}
                onClick={refund}
              >
                Refund {formatMoney(Math.min(Number(refundAmount) || 0, refundMaximum))}
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export function PatientPaymentsTab(): JSX.Element {
  const medplum = useMedplum();
  const [source, setSource] = useState<PaymentSource>('all');
  const [timeframe, setTimeframe] = useState<PaymentsTimeframe>('last-6-months');
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [encounterType, setEncounterType] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedBalance, setSelectedBalance] = useState<PaymentChargeEntry>();
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedAction, setSelectedAction] = useState<{ entry: PaymentChargeEntry; action: PaymentRowAction }>();
  const [refreshKey, setRefreshKey] = useState(0);
  const globalFilters: PaymentChargesFilters = {
    timeframe,
    source,
    status: statusFilter,
    encounterType,
    search,
    from: timeframe === 'custom' ? from : null,
    to: timeframe === 'custom' ? to : null,
  };
  const metricFilters: PaymentChargesFilters = {
    timeframe: 'all',
    source,
    status: null,
    encounterType: null,
    search: '',
    from: null,
    to: null,
  };
  const outstandingSummary = usePaymentCharges({
    variant: 'outstanding',
    page: 1,
    pageSize: 200,
    filters: metricFilters,
  });
  const upcomingSummary = usePaymentCharges({
    variant: 'upcoming',
    page: 1,
    pageSize: 200,
    filters: metricFilters,
  });
  const completeSummary = usePaymentCharges({
    variant: 'complete',
    page: 1,
    pageSize: 200,
    filters: metricFilters,
  });
  const archivedSummary = usePaymentCharges({
    variant: 'archived',
    page: 1,
    pageSize: 200,
    filters: metricFilters,
  });
  const availableStatuses = new Set([
    ...upcomingSummary.entries.map((entry) => formatStatus(entry, 'upcoming')),
    ...outstandingSummary.entries.map((entry) => formatStatus(entry, 'outstanding')),
    ...completeSummary.entries.map((entry) => formatStatus(entry, 'complete')),
    ...archivedSummary.entries.map((entry) => formatStatus(entry, 'archived')),
  ]);
  const statusOptions = STATUS_FILTER_ORDER.filter((status) => availableStatuses.has(status));
  const outstandingCents = outstandingSummary.entries.reduce((sum, entry) => sum + entry.balanceCents, 0);
  const awaitingPayment = outstandingSummary.entries.filter((entry) => entry.status === 'Billed').length;
  const balancesToRecord = upcomingSummary.entries.filter(
    (entry) =>
      entry.source === 'insured' &&
      entry.history.some((item) => item.event.toLowerCase().includes('claim submitted')) &&
      entry.history.some((item) => item.event.toLowerCase().includes('claim returned'))
  ).length;

  const openBalance = (entry: PaymentChargeEntry): void => {
    setSelectedBalance(entry);
    setDrawerOpened(true);
  };

  const openAction = (entry: PaymentChargeEntry, action: PaymentRowAction): void => {
    setSelectedAction({ entry, action });
  };

  const refresh = (): void => setRefreshKey((value) => value + 1);

  const applyClaimAction = async (entry: PaymentChargeEntry, action: 'submit' | 'returned'): Promise<void> => {
    if (!entry.chargeItemId || entry.chargeItemId.startsWith('encounter:')) {
      return;
    }
    try {
      const charge = await medplum.readResource('ChargeItem', entry.chargeItemId);
      await medplum.updateResource(
        appendHistory(
          charge,
          action === 'submit'
            ? `Claim submitted to ${entry.payer ?? 'payer'}`
            : `Claim returned by ${entry.payer ?? 'payer'}`
        )
      );
      showNotification({
        color: 'green',
        message:
          action === 'submit' ? 'Claim marked submitted.' : 'Claim marked returned. Record patient balance next.',
      });
      refresh();
      if (action === 'returned') {
        openBalance(entry);
      }
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    }
  };

  return (
    <Stack gap={0} className={styles.page}>
      <Paper className={styles.hero} radius="md">
        <Group justify="space-between" align="flex-start" gap={28} wrap="wrap">
          <Stack gap={0} className={styles.heroMain}>
            <Title order={1} c="white" className={styles.heroTitle}>
              Patient Payments
            </Title>
            <SegmentedControl
              aria-label="Payment source"
              className={styles.sourceTabs}
              data={SOURCE_OPTIONS}
              value={source}
              onChange={(value) => setSource(value as PaymentSource)}
            />
          </Stack>
          <Group gap={26} className={styles.metrics} wrap="wrap">
            <Metric
              label="Outstanding"
              value={outstandingSummary.loading ? '—' : formatMoney(outstandingCents / 100)}
            />
            <Metric label="Awaiting payment" value={outstandingSummary.loading ? '—' : String(awaitingPayment)} />
            <Metric label="Balances to record" value={outstandingSummary.loading ? '—' : String(balancesToRecord)} />
          </Group>
        </Group>
      </Paper>
      <Paper className={styles.filterBar}>
        <Select
          label="Timeframe"
          value={timeframe}
          data={[
            { value: 'this-month', label: 'This Month' },
            { value: 'last-7-days', label: 'Last 7 Days' },
            { value: 'last-30-days', label: 'Last 30 Days' },
            { value: 'last-90-days', label: 'Last 90 Days' },
            { value: 'last-3-months', label: 'Last 3 Months' },
            { value: 'last-6-months', label: 'Last 6 Months' },
            { value: 'last-12-months', label: 'Last 12 Months' },
            { value: 'year-to-date', label: 'Year to Date' },
            { value: 'all', label: 'All Time' },
            { value: 'custom', label: 'Custom range' },
          ]}
          onChange={(value) => setTimeframe((value ?? 'last-6-months') as PaymentsTimeframe)}
          comboboxProps={{ withinPortal: false, position: 'bottom-start', shadow: 'sm' }}
        />
        {timeframe === 'custom' && (
          <TextInput
            label="From"
            type="date"
            value={from ?? ''}
            onChange={(event) => setFrom(event.currentTarget.value || null)}
          />
        )}
        {timeframe === 'custom' && (
          <TextInput
            label="To"
            type="date"
            value={to ?? ''}
            onChange={(event) => setTo(event.currentTarget.value || null)}
          />
        )}
        <Select
          label="Status"
          placeholder="All"
          clearable
          value={statusFilter}
          data={statusOptions}
          onChange={setStatusFilter}
          comboboxProps={{ withinPortal: false, position: 'bottom-start', shadow: 'sm' }}
        />
        <Select
          label="Encounter Type"
          placeholder="All"
          clearable
          value={encounterType}
          data={[
            'New Patient Intake',
            'Get Answers Bundle',
            'Home Sleep Test',
            'Follow-up & Results Review',
            'RPM device setup',
            'Remote Patient Monitoring',
          ]}
          onChange={setEncounterType}
          comboboxProps={{ withinPortal: false, position: 'bottom-start', shadow: 'sm' }}
        />
        <TextInput
          label="Patient"
          placeholder="Name, ID, or charge #"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
      </Paper>
      {source !== 'self-pay' && (
        <PaymentsSection
          key={`upcoming-${refreshKey}`}
          variant="upcoming"
          source={source}
          onBalanceAction={openBalance}
          onAction={openAction}
          canRecordBalance
          globalFilters={globalFilters}
          showFilters={false}
          onClaimAction={applyClaimAction}
        />
      )}
      <PaymentsSection
        key={`outstanding-${refreshKey}`}
        variant="outstanding"
        source={source}
        onBalanceAction={openBalance}
        onAction={openAction}
        canRecordBalance
        globalFilters={globalFilters}
        showFilters={false}
        onClaimAction={applyClaimAction}
      />
      <PaymentsSection
        key={`complete-${refreshKey}`}
        variant="complete"
        source={source}
        onBalanceAction={openBalance}
        onAction={openAction}
        canRecordBalance
        globalFilters={globalFilters}
        showFilters={false}
        onClaimAction={applyClaimAction}
      />
      <PaymentsSection
        key={`archived-${refreshKey}`}
        variant="archived"
        source={source}
        onAction={openAction}
        canRecordBalance
        globalFilters={globalFilters}
        showFilters={false}
        onClaimAction={applyClaimAction}
      />
      <BalanceDrawer
        entry={selectedBalance}
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        onSaved={refresh}
      />
      <PaymentActionModal
        entry={selectedAction?.entry}
        action={selectedAction?.action}
        opened={!!selectedAction}
        onClose={() => setSelectedAction(undefined)}
        onChanged={refresh}
        onRefundRequested={() => {
          if (selectedAction) {
            setSelectedAction({ ...selectedAction, action: 'refund' });
          }
        }}
        onClaimReturned={() => {
          if (selectedAction) {
            setSelectedBalance(selectedAction.entry);
            setDrawerOpened(true);
          }
        }}
      />
    </Stack>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack gap={0} className={styles.metric}>
      <Text size="xs" fw={700} tt="uppercase" className={styles.metricLabel}>
        {label}
      </Text>
      <Text size="xl" fw={800} c="white" className={styles.metricValue}>
        {value}
      </Text>
    </Stack>
  );
}
